import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGetRequest, usePostRequest } from '@/shared/hooks/requests';
import { FILE_COMMENTS, COMMENT_RESOLVE, COMMENT_UNRESOLVE, COMMENT_DELETE } from '@/shared/utils/urls';
import { useTranslation } from 'react-i18next';
import baseAxios from '@/shared/utils/request';
import { auth } from '@/shared/utils/auth';
import {
  MessageSquare,
  Send,
  CornerDownRight,
  Loader2,
  CheckCircle,
  Circle,
} from 'lucide-react';
import { toast } from 'sonner';

// The server's cap on a comment (MAX_COMMENT_LENGTH).
const COMMENT_MAX = 10000;

/**
 * CommentsPanel - Shows comments for a document with reply and resolve functionality
 */
export function CommentsPanel({ open, onOpenChange, fileId }) {
  const { t } = useTranslation('my-docs');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isResolving, setIsResolving] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);
  const textareaRef = useRef(null);

  const getComments = useGetRequest({
    url: FILE_COMMENTS.replace('{id}', fileId),
  });

  const createComment = usePostRequest({
    url: FILE_COMMENTS.replace('{id}', fileId),
  });

  useEffect(() => {
    if (open && fileId) {
      loadComments();
    }
  }, [open, fileId]);

  const loadComments = async () => {
    const { response, success } = await getComments.request();
    if (success && response?.comments) {
      setComments(response.comments);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    const { success } = await createComment.request({
      data: { content: newComment.trim() },
    });

    if (success) {
      setNewComment('');
      loadComments();
      toast.success(t('comments.added'));
    } else {
      toast.error(t('comments.addError'));
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!replyContent.trim()) return;

    const { success } = await createComment.request({
      data: {
        content: replyContent.trim(),
        parent_id: parentId,
      },
    });

    if (success) {
      setReplyContent('');
      setReplyTo(null);
      loadComments();
      toast.success(t('comments.replyAdded'));
    } else {
      toast.error(t('comments.replyError'));
    }
  };

  const handleResolve = useCallback(async (commentId, isResolved) => {
    const url = isResolved
      ? COMMENT_UNRESOLVE.replace('{id}', commentId)
      : COMMENT_RESOLVE.replace('{id}', commentId);

    setIsResolving(commentId);
    try {
      await baseAxios.post(url, {}, auth());
      loadComments();
      toast.success(
        isResolved ? t('comments.reopened') : t('comments.resolved')
      );
    } catch (error) {
      toast.error(t('comments.resolveError'));
    } finally {
      setIsResolving(null);
    }
  }, [t]);

  const handleDelete = useCallback(async (commentId) => {
    if (!window.confirm(t('comments.confirmDelete'))) return;

    setIsDeleting(commentId);
    try {
      await baseAxios.delete(COMMENT_DELETE.replace('{id}', commentId), auth());
      loadComments();
      toast.success(t('comments.deleted'));
    } catch (error) {
      toast.error(t('comments.deleteError'));
    } finally {
      setIsDeleting(null);
    }
  }, [t]);

  // Get user initials
  const getUserInitials = (user) => {
    if (!user) return '?';
    const name = user.fullName || user.email || '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format relative time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return t('time.justNow');
    if (diffMin < 60) return t('time.minutesAgo', { count: diffMin });
    if (diffHour < 24) return t('time.hoursAgo', { count: diffHour });
    if (diffDay < 7) return t('time.daysAgo', { count: diffDay });

    return date.toLocaleDateString('ru-RU');
  };

  const CommentItem = ({ comment, isReply = false }) => (
    <div
      className={`${isReply ? 'ml-8 border-l-2 border-gray-100 pl-4' : ''} ${comment.is_resolved ? 'opacity-60' : ''
        }`}
    >
      <div className="flex gap-3 py-3">
        <Avatar className="w-8 h-8 flex-shrink-0">
          <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
            {getUserInitials(comment.user)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="font-medium text-sm">
                {comment.user?.fullName || comment.user?.email || t('comments.unknownUser')}
              </span>
              <span className="text-xs text-gray-400 ml-2">
                {formatTime(comment.created_at)}
              </span>
            </div>

            {/* Actions */}
            {!isReply && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleResolve(comment.id, comment.is_resolved)}
                  title={comment.is_resolved ? t('comments.reopen') : t('comments.resolve')}
                >
                  {comment.is_resolved ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Selection text highlight */}
          {comment.selection_text && (
            <div className="my-2 px-3 py-2 bg-yellow-50 border-l-4 border-yellow-400 text-sm text-gray-700 italic">
              "{comment.selection_text}"
            </div>
          )}

          {/* Comment content */}
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
            {comment.content}
          </p>

          {/* Reply button */}
          {!isReply && !comment.is_resolved && (
            <button
              onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
              className="text-xs text-blue-600 hover:text-blue-700 mt-2 flex items-center gap-1"
            >
              <CornerDownRight className="w-3 h-3" />
              {t('comments.reply')}
            </button>
          )}

          {/* Reply input */}
          {replyTo === comment.id && (
            <div className="mt-3 flex gap-2">
              <Textarea
                placeholder={t('comments.replyPlaceholder')}
                value={replyContent}
                maxLength={COMMENT_MAX}
                onChange={(e) => setReplyContent(e.target.value)}
                className="min-h-[60px] text-sm"
              />
              <Button
                size="sm"
                onClick={() => handleSubmitReply(comment.id)}
                disabled={!replyContent.trim()}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} isReply />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[540px] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {t('comments.title')}
            {comments.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({comments.length})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {/* New comment input */}
        <div className="mt-4 pb-4 border-b">
          <Textarea
            ref={textareaRef}
            placeholder={t('comments.placeholder')}
            value={newComment}
            maxLength={COMMENT_MAX}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[80px]"
          />
          <div className="flex justify-end mt-2">
            <Button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || createComment.loading}
              size="sm"
            >
              {createComment.loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {t('comments.send')}
            </Button>
          </div>
        </div>

        {/* Comments list */}
        <div className="flex-1 mt-4 -mx-6">
          {getComments.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('comments.noComments')}</p>
              <p className="text-sm mt-1">
                {t('comments.beFirst')}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100dvh-300px)] px-6">
              <div className="divide-y">
                {comments.map((comment) => (
                  <CommentItem key={comment.id} comment={comment} />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
